<script lang="ts">
  /**
   * One row of an options sheet: an icon, a title, one line saying what it
   * does. The `danger` tone is for the rows that cannot be taken back, and
   * belongs in an `OptionSection` of its own.
   *
   * Without `onclick` the row is a line of information rather than a button
   * (a profile sheet's coin balance): the same shape, nothing to press.
   *
   * Since 1.7.0, `disabled`: a row that cannot be pressed right now
   * (somebody else's turn, an allowance spent). It stays a button and stays
   * in the tab order, so the reason can be reached and read, but it is
   * `aria-disabled` and a press calls nothing. `disabledReason` takes the
   * second line's place while it is disabled ("Wait for your turn") and is
   * read as part of the row's name; the title and the glyph quieten.
   *
   * Test ids: `testid` on the row, `detailTestid` (since 1.7.0) on its
   * second line (the detail, or the reason while disabled). No defaults.
   */
  import type { Snippet } from 'svelte';

  interface Props {
    readonly title: string;
    readonly detail?: string | undefined;
    readonly icon?: Snippet | undefined;
    readonly tone?: 'default' | 'danger';
    readonly onclick?: (() => void) | undefined;
    readonly testid?: string | undefined;
    // ── 1.7.0 ──
    /** Cannot be pressed right now: `aria-disabled`, still focusable, a press calls nothing. */
    readonly disabled?: boolean;
    /** Why, in the second line's place while `disabled` ("Wait for your turn"). */
    readonly disabledReason?: string | undefined;
    /** On the second line (the detail, or the reason while disabled). */
    readonly detailTestid?: string | undefined;
  }
  const {
    title,
    detail,
    icon,
    tone = 'default',
    onclick,
    testid,
    disabled = false,
    disabledReason,
    detailTestid,
  }: Props = $props();

  const line = $derived(disabled && disabledReason !== undefined ? disabledReason : detail);

  function press(): void {
    if (!disabled) onclick?.();
  }
</script>

{#snippet content()}
  {#if icon !== undefined}
    <span class="ui-option-icon" aria-hidden="true">{@render icon()}</span>
  {/if}
  <span class="ui-option-text">
    <b>{title}</b>
    {#if line !== undefined}
      <span data-testid={detailTestid}>{line}</span>
    {/if}
  </span>
{/snippet}

{#if onclick !== undefined}
  <button
    type="button"
    class="ui-option-row"
    class:ui-option-row--danger={tone === 'danger'}
    class:ui-option-row--disabled={disabled}
    aria-disabled={disabled ? 'true' : undefined}
    data-testid={testid}
    onclick={press}
  >
    {@render content()}
  </button>
{:else}
  <div
    class="ui-option-row ui-option-row--static"
    class:ui-option-row--disabled={disabled}
    data-testid={testid}
  >
    {@render content()}
  </div>
{/if}

<style>
  .ui-option-row {
    display: flex;
    align-items: center;
    gap: var(--ui-space-3);
    width: 100%;
    min-height: 3.5rem;
    padding: var(--ui-space-2) 0.1rem;
    border: 0;
    border-bottom: 1px solid var(--ui-line);
    background: transparent;
    text-align: left;
    color: var(--ui-ink);
    font-family: var(--ui-font);
  }

  button.ui-option-row:focus-visible {
    outline: 3px solid var(--ui-accent);
    outline-offset: -3px;
  }

  .ui-option-row:last-child {
    border-bottom: 0;
  }

  .ui-option-icon {
    flex: 0 0 auto;
    width: 2.5rem;
    height: 2.5rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--ui-radius-sm);
    --ui-icon-cut: var(--ui-surface-2);
    background: var(--ui-icon-cut);
    color: var(--ui-accent);
  }

  .ui-option-text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
  }

  .ui-option-text b {
    font: var(--ui-text-body);
    font-weight: 800;
    color: var(--ui-ink);
  }

  .ui-option-text span {
    font: var(--ui-text-caption);
    color: var(--ui-ink-2);
  }

  .ui-option-row--danger .ui-option-icon {
    color: var(--ui-danger);
    background: color-mix(in srgb, var(--ui-danger) 14%, transparent);
  }

  .ui-option-row--danger .ui-option-text b {
    color: var(--ui-danger);
  }

  /* Cannot be pressed right now (1.7.0): the glyph fades and the title
     quietens to the second line's grey (a gated pair, never an opacity on
     words); the reason keeps its contrast, since it is what the row says. */
  .ui-option-row--disabled .ui-option-icon {
    opacity: 0.5;
  }

  .ui-option-row--disabled .ui-option-text b {
    color: var(--ui-ink-2);
  }
</style>
